import type { PessoaRef, ResultadoGeracao } from './organogramaGerador';

/**
 * Avisos da revisão do "Gerar organograma a partir dos cadastros", com os atalhos para corrigir cada um.
 * Função PURA (sem React; import relativo) — testada em backend/tests/organograma-gerador-avisos.test.ts.
 */

/** Quantos nomes aparecem por aviso antes do "e mais N" (cada nome abre a ficha da pessoa). */
export const LIMITE_NOMES = 8;

/** Ficha do usuário já na aba onde se corrigem departamento, cargo e gestor. */
export const fichaDoUsuario = (usuarioId: string) => `/usuarios/${usuarioId}?modulo=acesso-empresas`;

export interface AtalhoAviso {
  rotulo: string;
  destino: string;
}

export interface ItemAviso {
  chave: string;
  texto: string;
  detalhe?: string;
  /** Pessoas citadas no aviso: cada nome vira atalho para a ficha dela. */
  pessoas?: PessoaRef[];
  separador?: string;
  /** Atalhos para as telas onde o problema se corrige (Departamentos, Cargos, Usuários...). */
  acoes?: AtalhoAviso[];
  informativo?: boolean;
}

export const ABRIR_DEPARTAMENTOS: AtalhoAviso = { rotulo: 'Abrir Departamentos', destino: '/departamentos' };
export const ABRIR_CARGOS: AtalhoAviso = { rotulo: 'Abrir Cargos', destino: '/cargos' };
export const ABRIR_USUARIOS: AtalhoAviso = { rotulo: 'Abrir Usuários', destino: '/usuarios' };

export function montarAvisos(resultado: ResultadoGeracao): ItemAviso[] {
  const { avisos } = resultado;
  const itens: ItemAviso[] = [];
  const plural = (n: number, singular: string, pluralTxt: string) => `${n} ${n === 1 ? singular : pluralTxt}`;

  if (avisos.semDepartamento.length > 0) {
    itens.push({
      chave: 'semDepartamento',
      texto: `${plural(avisos.semDepartamento.length, 'pessoa sem departamento válido', 'pessoas sem departamento válido')} (vão para "Sem departamento" se também não tiverem gestor)`,
      pessoas: avisos.semDepartamento,
    });
  }
  if (avisos.semCargo.length > 0) {
    itens.push({
      chave: 'semCargo',
      texto: `${plural(avisos.semCargo.length, 'pessoa sem cargo', 'pessoas sem cargo')} (o card fica sem subtítulo)`,
      pessoas: avisos.semCargo,
    });
  }
  if (avisos.gestorInvalido.length > 0) {
    itens.push({
      chave: 'gestorInvalido',
      texto: `${plural(avisos.gestorInvalido.length, 'pessoa com gestor inválido', 'pessoas com gestor inválido')} (gestor inativo ou removido — voltam para o departamento)`,
      pessoas: avisos.gestorInvalido,
    });
  }
  avisos.ciclos.forEach((ciclo, indice) => {
    itens.push({
      chave: `ciclo-${indice}`,
      texto: 'Ciclo de gestores quebrado (as pessoas voltam para o departamento)',
      pessoas: ciclo,
      separador: ' ↔ ',
    });
  });
  if (avisos.cargosPeloQuadro.length > 0) {
    const pessoasQuadro = avisos.cargosPeloQuadro.reduce((soma, c) => soma + c.quantidade, 0);
    itens.push({
      chave: 'cargosPeloQuadro',
      texto: `${plural(avisos.cargosPeloQuadro.length, 'cargo entra', 'cargos entram')} pela quantidade do quadro (${plural(pessoasQuadro, 'pessoa não cadastrada', 'pessoas não cadastradas')})`,
      detalhe: avisos.cargosPeloQuadro.map((c) => `${c.nome} (${c.quantidade})`).join(', '),
      acoes: [ABRIR_CARGOS],
      informativo: true,
    });
  }
  if (avisos.cargosAbaixoDoQuadro.length > 0) {
    itens.push({
      chave: 'cargosAbaixoDoQuadro',
      texto: `${plural(avisos.cargosAbaixoDoQuadro.length, 'cargo tem', 'cargos têm')} menos pessoas cadastradas que o quadro (só as cadastradas aparecem)`,
      detalhe: avisos.cargosAbaixoDoQuadro.map((c) => `${c.nome}: ${c.cadastradas} de ${c.quadro}`).join(', '),
      acoes: [ABRIR_USUARIOS, ABRIR_CARGOS],
      informativo: true,
    });
  }
  if (avisos.departamentosSemPessoas.length > 0) {
    itens.push({
      chave: 'departamentosSemPessoas',
      texto: `${plural(avisos.departamentosSemPessoas.length, 'departamento sem pessoas', 'departamentos sem pessoas')} (não geram card)`,
      detalhe: avisos.departamentosSemPessoas.join(', '),
      acoes: [ABRIR_DEPARTAMENTOS],
    });
  }
  if (avisos.departamentosSemRaiz.length > 0) {
    itens.push({
      chave: 'departamentosSemRaiz',
      texto: `${plural(avisos.departamentosSemRaiz.length, 'departamento só com pessoas sob gestores de outras áreas', 'departamentos só com pessoas sob gestores de outras áreas')} (as pessoas aparecem sob os gestores; o departamento só ganha card se tiver cargo do quadro)`,
      detalhe: avisos.departamentosSemRaiz.join(', '),
      acoes: [ABRIR_DEPARTAMENTOS],
    });
  }
  if (avisos.departamentoPeloCargo.length > 0) {
    itens.push({
      chave: 'departamentoPeloCargo',
      texto: `${plural(avisos.departamentoPeloCargo.length, 'pessoa usa', 'pessoas usam')} o departamento do cargo (não têm departamento próprio no cadastro)`,
      pessoas: avisos.departamentoPeloCargo,
      informativo: true,
    });
  }
  if (avisos.semGestor.length > 0) {
    itens.push({
      chave: 'semGestor',
      texto: `${plural(avisos.semGestor.length, 'pessoa sem gestor', 'pessoas sem gestor')} (ficam abaixo do departamento — normal no topo de cada área)`,
      pessoas: avisos.semGestor,
      informativo: true,
    });
  }
  if (avisos.cargosSemPessoas.length > 0) {
    itens.push({
      chave: 'cargosSemPessoas',
      texto: `${plural(avisos.cargosSemPessoas.length, 'cargo sem ninguém', 'cargos sem ninguém')}`,
      detalhe: avisos.cargosSemPessoas.join(', '),
      acoes: [ABRIR_CARGOS],
      informativo: true,
    });
  }
  // Os que pedem correção primeiro; os informativos (que podem ter listas longas, como "sem gestor") no fim.
  return [...itens.filter((i) => !i.informativo), ...itens.filter((i) => i.informativo)];
}
